import { localStorageService } from "@/core/storage";

import {
  BANNER_POLICY_PROVIDERS,
  type BannerPolicy,
  type ConsentAnswerProvenance,
  type ConsentDecision,
  type ResolvedConsentStatus,
} from "@/core/consent/domain/consent.types";
import type { ConsentSource } from "@/core/consent/sources/consent.source";

/**
 * Persisted consent ANSWER for the instrumentation stack. A NEW key on
 * purpose: the legacy consent module's keys encode legacy shapes and legacy
 * dedup semantics, and sharing them would couple the two stacks during the
 * migration window. Everything this stack persists lives under
 * `ageless.instrumentation.*`.
 */
export const CONSENT_DECISION_STORAGE_KEY = "ageless.instrumentation.consent.decision";

/**
 * The persisted shape: the visitor's explicit answer and its own provenance,
 * NEVER the GPC-clamped effective decision (see `ResolvedConsentStatus`).
 * `noticeVersion` rides along so the REDUCER can reject an answer made under
 * an older notice (re-prompt by construction) — the source loads blindly and
 * never needs to know what "current" is.
 */
type StoredConsentDecision = {
  readonly decision: ConsentDecision;
  readonly provenance: ConsentAnswerProvenance;
  readonly noticeVersion: string;
  readonly bannerPolicy: BannerPolicy | null;
};

/**
 * Only answer provenances are valid on disk. A persisted `gpc` cannot occur
 * (the clamp is derived per session, never stored), so any other value is
 * treated as corrupt and ignored rather than replayed into the engine.
 */
const ANSWER_PROVENANCES: readonly ConsentAnswerProvenance[] = ["banner", "override"];

const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

/**
 * A stored policy that is missing or malformed loads as `null` without
 * discarding the answer: the answer is still the visitor's, and "no policy
 * kept" is the honest description of an unreadable one.
 */
const parseBannerPolicy = (value: unknown): BannerPolicy | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const candidate = value as Partial<BannerPolicy>;
  if (
    candidate.provider === undefined ||
    !BANNER_POLICY_PROVIDERS.includes(candidate.provider) ||
    typeof candidate.id !== "string" ||
    candidate.id.length === 0 ||
    typeof candidate.mode !== "string" ||
    candidate.mode.length === 0
  ) {
    return null;
  }

  return { provider: candidate.provider, id: candidate.id, mode: candidate.mode };
};

/**
 * Structural validation, not trust: localStorage is writable by anything on
 * the origin and survives deploys that change our own shapes. A partial or
 * corrupt value is treated as "no stored decision" — the visitor gets
 * re-prompted, which is always the safe failure — never thrown on.
 */
const parseStoredDecision = (value: unknown): StoredConsentDecision | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const candidate = value as Partial<StoredConsentDecision>;
  const decision = candidate.decision as Partial<ConsentDecision> | undefined;

  if (
    typeof decision !== "object" ||
    decision === null ||
    !isBoolean(decision.functional) ||
    !isBoolean(decision.analytics) ||
    !isBoolean(decision.advertising) ||
    !isBoolean(decision.dataSharing) ||
    typeof candidate.noticeVersion !== "string" ||
    !ANSWER_PROVENANCES.includes(candidate.provenance as ConsentAnswerProvenance)
  ) {
    return null;
  }

  return {
    decision: {
      functional: decision.functional,
      analytics: decision.analytics,
      advertising: decision.advertising,
      dataSharing: decision.dataSharing,
    },
    provenance: candidate.provenance as ConsentAnswerProvenance,
    noticeVersion: candidate.noticeVersion,
    bannerPolicy: parseBannerPolicy(candidate.bannerPolicy),
  };
};

/**
 * Emits `StoredDecisionLoaded` for a valid persisted decision, synchronously
 * on start. Load-only by design: persisting NEW resolutions is the
 * engine's job (see `createConsentEngine`), so the write and the
 * state it snapshots can never disagree.
 */
export const createStorageSource = (): ConsentSource => ({
  start(emit) {
    const stored = parseStoredDecision(
      localStorageService.getItemSync<unknown>(CONSENT_DECISION_STORAGE_KEY),
    );

    if (stored) {
      emit({ type: "storedDecisionLoaded", ...stored });
    }

    return () => {};
  },
});

/**
 * Persist a resolved state's ANSWER under the schema this source loads. Lives
 * beside the loader so the round-trip shape has exactly one owner; called by
 * the composition root each time a resolution changes. Deliberately not the
 * effective decision: a GPC clamp is a per-session derivation, and writing it
 * here would keep the visitor denied after they switch GPC off. A banner
 * policy's default is never written either: it is not the visitor's answer,
 * and each visit works it out from the policy again.
 */
export const persistResolvedConsent = (state: ResolvedConsentStatus): void => {
  if (state.answerProvenance === "policyDefault") {
    return;
  }

  const stored: StoredConsentDecision = {
    decision: state.answer,
    provenance: state.answerProvenance,
    noticeVersion: state.noticeVersion,
    bannerPolicy: state.answerBannerPolicy,
  };

  localStorageService.setItemSync(CONSENT_DECISION_STORAGE_KEY, stored);
};
